"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RaydiumSwapAction = void 0;
const sdk_1 = require("@malloc-protocol/sdk");
const web3_js_1 = require("@solana/web3.js");
const cpi_action_json_1 = __importDefault(require("../../target/idl/cpi_action.json"));
const raydium_sdk_1 = require("@raydium-io/raydium-sdk");
const { getTokenAccountUsedByMalloc } = sdk_1.utils.tokens;
const cpi_action_1 = require("../utils/cpi-action");
const bn_js_1 = __importDefault(require("bn.js"));
const raydium_utils_1 = require("./raydium-utils");
const actionTypeUID = "MALLOC_SPL_RAYDIUM_SWAP";
const buildIsolatedRaydiumSwap = ({ inp, inputTokens: inpTokAccounts, actionPID, provider, authority, opts, }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    if (inpTokAccounts.length !== 1)
        throw `Swapping expects only 1 input mint`;
    const dummyIn = new bn_js_1.default(0);
    let baseTokenAccount;
    let quoteTokenAccount;
    if (!inp.liquidityPool && !inp.outMint)
        throw `Expected to get a liquidity pool input or a token mint input`;
    const outMint = inp.outMint ? new web3_js_1.PublicKey(inp.outMint) : undefined;
    const liquidityPool = (_a = inp.liquidityPool) !== null && _a !== void 0 ? _a : (yield (0, raydium_utils_1.getMainnetRaydiumPools)({
        matchesMints: [outMint, inpTokAccounts[0].mint],
        version: 4,
    }))[0]; // TODO: this just fetches the first liquidity pool found, it may not be the best
    if (!liquidityPool)
        throw `Could not find a valid liquidity pool for mints ${outMint.toBase58()} and ${inpTokAccounts[0].mint.toBase58()}`;
    const { baseMint, quoteMint } = liquidityPool;
    if (!baseMint.equals(inpTokAccounts[0].mint) &&
        !quoteMint.equals(inpTokAccounts[0].mint))
        throw `Expected the input token to match the input liquidity pool`;
    const inputIsBase = baseMint.toBase58() === inpTokAccounts[0].mint.toBase58();
    if (inputIsBase) {
        baseTokenAccount = inpTokAccounts[0].tokenAccount;
        quoteTokenAccount = yield getTokenAccountUsedByMalloc(authority, quoteMint, opts.preferredTokenAccounts);
    }
    else {
        quoteTokenAccount = inpTokAccounts[0].tokenAccount;
        baseTokenAccount = yield getTokenAccountUsedByMalloc(authority, baseMint, opts.preferredTokenAccounts);
    }
    const side = inputIsBase ? "sell" : "buy";
    const swapParams = {
        poolKeys: liquidityPool,
        userKeys: {
            baseTokenAccount,
            quoteTokenAccount,
            owner: authority,
        },
        side,
        minAmountOut: dummyIn,
        amountIn: dummyIn,
    };
    const mintOut = inputIsBase ? quoteMint : baseMint;
    const toCoinAccount = inputIsBase ? quoteTokenAccount : baseTokenAccount;
    const inst = raydium_sdk_1.Liquidity.makeSwapInstruction(swapParams);
    const mintIn = new web3_js_1.PublicKey(inpTokAccounts[0].mint);
    return {
        estimateOuts: (amounts) => __awaiter(void 0, void 0, void 0, function* () {
            const estimate = yield getSwapOutAmount(liquidityPool, amounts[0].mint, new bn_js_1.default(amounts[0].amount), 0, provider.connection);
            return [
                {
                    mint: mintOut,
                    amount: estimate,
                },
            ];
        }),
        isolatedAction: {
            actionTypeUID,
            tokenMintIn: mintIn,
            actionData: (0, cpi_action_1.packInstrData)(inst.data, cpi_action_1.InstructionType.RAYDIUM_SWAP),
            actionProgram: new web3_js_1.PublicKey(actionPID),
            actionAccounts: [
                { address: inst.programId },
                ...sdk_1.utils.object.actionMetasToAccountsArray(inst.keys),
            ],
            tokenMintOuts: [mintOut],
            tokenAccountOuts: [toCoinAccount],
            opts: {
                // Disabling these checks allow for just enough gas to run the swaps:
                // we trust the out tokens because it should be calculated via
                // the cpi check
                skipOutTokenCheck: true,
                skipInTokenCheck: true,
            },
        },
    };
});
exports.RaydiumSwapAction = {
    buildIsolatedAction: buildIsolatedRaydiumSwap,
    actionTypeUID,
    expectedNumberOutMints: 1,
    idl: cpi_action_json_1.default,
};
const getSwapOutAmount = (poolInfo, fromCoinMint, amount, slippage, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const layout = raydium_sdk_1.Liquidity.getStateLayout(poolInfo.version);
    const state = yield connection.getAccountInfo(poolInfo.id);
    const [baseTokenAccount, quoteTokenAccount] = yield Promise.all([
        sdk_1.utils.tokens.getTokenAccountInfo(connection, poolInfo.baseVault),
        sdk_1.utils.tokens.getTokenAccountInfo(connection, poolInfo.quoteVault),
    ]);
    const coinBalance = baseTokenAccount.amount;
    const pcBalance = quoteTokenAccount.amount;
    const poolState = layout.decode(state.data);
    const { baseMint: coin, quoteMint: pc } = poolInfo;
    const { swapFeeNumerator, swapFeeDenominator } = poolState;
    if (fromCoinMint.equals(coin)) {
        // coin2pc
        const fromAmountWithFee = amount
            .mul(swapFeeDenominator.sub(swapFeeNumerator))
            .div(swapFeeDenominator);
        const denominator = coinBalance.add(fromAmountWithFee);
        const amountOut = pcBalance.mul(fromAmountWithFee).div(denominator);
        const amountOutWithSlippage = amountOut.divn(1 + slippage / 100);
        return amountOutWithSlippage;
    }
    else if (fromCoinMint.equals(pc)) {
        // pc2coin
        const fromAmountWithFee = amount
            .mul(swapFeeDenominator.sub(swapFeeNumerator))
            .div(swapFeeDenominator);
        const denominator = pcBalance.add(fromAmountWithFee);
        const amountOut = coinBalance.mul(fromAmountWithFee).div(denominator);
        const amountOutWithSlippage = amountOut.divn(1 + slippage / 100);
        return amountOutWithSlippage;
    }
    else {
        throw `An unexpected error occurred in estimating the swap out, expected the input token mint to be either a base mint or a quote mint`;
    }
});
